/**
 * Utility functions for formatting numbers, currency values, and dates
 */

import { format } from 'date-fns';

/**
 * Format a number as currency in Vietnamese format
 * @param value - The number to format
 * @returns Formatted currency string
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND'
  }).format(value);
}

/**
 * Format a number in Vietnamese format
 * @param value - The number to format
 * @returns Formatted number string
 */

export function formatMoney(value: number | string): string {
  if (value == null) return '';

  const numValue = typeof value === 'string' ? Number(value) : value;

  if (isNaN(numValue)) return '';

  const [intPart, decimalPart] = String(numValue).split('.');

  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;
}

export const formatNumber = (num: number | string): string => {
  return formatMoney(num);
};

export function formatRoundedNumber(value: number | string | null | undefined, precision: number = 0): string {
  return formatMoney(roundNumber(value, precision));
}
/**
 * Format a number as currency in Vietnamese format and replace the currency symbol with 'VND'
 * @param value - The number to format
 * @returns Formatted currency string with 'VND' instead of '₫'
 */
export function formatCurrencyWithVND(value: number): string {
  return formatCurrency(value).replace('₫', 'VND');
}

/**
 * Format a date to dd/MM/yyyy format
 * @param date - The date to format (can be Date object, string, or null/undefined)
 * @returns Formatted date string in dd/MM/yyyy format, or empty string if invalid
 */

export function formatDate(date: Date | string | null | undefined, type: 'date' | 'dateTime' = 'date'): string {
  if (!date) return '';

  try {
    let dateObj: Date;

    if (typeof date === 'string') {
      // Handle yyyymmdd format (e.g., "20240315")
      if (date.length === 8 && /^\d{8}$/.test(date)) {
        const year = parseInt(date.substring(0, 4));
        const month = parseInt(date.substring(4, 6)) - 1; // Month is 0-indexed
        const day = parseInt(date.substring(6, 8));
        dateObj = new Date(year, month, day);
      }
      // Handle ISO or dash/slash formats
      else {
        dateObj = new Date(date);
      }
    } else {
      dateObj = date;
    }

    // Check if valid date
    if (isNaN(dateObj.getTime())) return '';

    // Format theo type
    if (type === 'dateTime') {
      return format(dateObj, 'dd/MM/yyyy HH:mm:ss');
    }
    return format(dateObj, 'dd/MM/yyyy');
  } catch {
    return '';
  }
}

/**
 * Round a number to a specific precision
 * @param value - The value to round
 * @param precision - Number of decimal places (default is 6)
 * @returns Rounded number
 */
export function roundNumber(value: number | string | null | undefined, precision: number = 6): number {
  if (value == null || value === '') return 0;
  const num = typeof value === 'number' ? value : Number(value);
  if (isNaN(num)) return 0;
  const factor = Math.pow(10, precision);
  return Math.round(num * factor) / factor;
}

export function toFiniteNumber(value: unknown, fallback: number = 0): number {
  if (value == null || value === '') return fallback;

  const num = Number(value);

  return Number.isFinite(num) ? num : fallback;
}
